import { Checkbox, removeDiacritics, selectMenuStyles, theme } from '@arnold/common';
import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';
import { TeamWithSubTeams } from './SupervisorTeamSelect';
import { TeamsWithSubTeams } from './TeamsAndPeople';

interface ICustomMenuProps {
  teams: TeamsWithSubTeams;
  value: string | string[];
  onChange: (id: string) => void;
  search?: string;
  multiselect?: boolean;
  topTeamName?: string;
  permanentlyEnabledTeamIds?: string[];
}

interface ITeamProps {
  value: string | string[];
  level: number;
  team: TeamWithSubTeams;
  onChange: (id: string) => void;
  search?: string;
  multiselect?: boolean;
  permanentlyEnabledTeamIds?: string[];
}

interface IItemContainer {
  level: number;
  highlight: boolean;
  selected: boolean;
  disabled?: boolean;
}

const OptionName = styled.span`
  white-space: nowrap;
  display: flex;
  align-items: center;
  width: 100%;
`;

const Empty = styled.div`
  padding: ${theme.spacing.e} ${theme.spacing.f};
  color: ${theme.colors.text.secondary};
`;

const ItemContainer = styled.div<IItemContainer>`
  padding-left: ${({ level }) => 18 + level * 16}px;
  font-weight: ${({ highlight }) => (highlight ? '700' : '400')};
  padding-top: ${theme.spacing.d};
  padding-bottom: ${theme.spacing.d};
  cursor: pointer;
  background: ${theme.colors.backgroundCover.default};
  transition: background 0.25s ease;
  &:hover {
    background: ${theme.colors.backgroundBasic.default};
  }

  color: ${({ selected }) => (selected ? theme.colors.actionPrimary.default : theme.colors.text.primary)};

  &:active {
    color: ${({ selected }) => (selected ? theme.colors.actionInvertedPrimary.active : theme.colors.text.primary)};
    background-color: #2684ff;
  }
`;

const TeamItem = React.memo(
  ({ team, value, onChange, level, search, multiselect, permanentlyEnabledTeamIds = [] }: ITeamProps) => {
    const onClick = () => {
      if (permanentlyEnabledTeamIds.includes(team.id)) {
        return;
      }

      onChange(team.id);
    };

    const onCheckboxToggle = (event: React.MouseEvent) => {
      event.stopPropagation();
      if (permanentlyEnabledTeamIds.includes(team.id)) {
        return;
      }

      onChange(team.id);
    };

    const highlight =
      search?.length &&
      removeDiacritics(team.name?.toLowerCase() || '').includes(removeDiacritics(search.toLowerCase()));

    const isSelected = multiselect ? Array.isArray(value) && value.includes(team.id) : team.id === value;

    return (
      <div key={`team-${team.id}`}>
        <ItemContainer level={level} highlight={!!highlight} onClick={onClick} selected={isSelected}>
          <OptionName>
            {multiselect ? (
              <Checkbox
                selected={isSelected || permanentlyEnabledTeamIds.includes(team.id)}
                onToggle={onCheckboxToggle}
                disabled={permanentlyEnabledTeamIds.includes(team.id)}
                text={`${team.name} ${team.numberOfEmployees !== undefined ? `(${team.numberOfEmployees})` : ''}`}
              />
            ) : (
              `${team.name} ${team.numberOfEmployees !== undefined ? `(${team.numberOfEmployees})` : ''}`
            )}
          </OptionName>
        </ItemContainer>
        {team.subTeams &&
          team.subTeams.map((newTeam) => {
            return (
              <TeamItem
                level={level + 1}
                key={newTeam.id}
                team={newTeam}
                value={value}
                onChange={onChange}
                search={search}
                multiselect={multiselect}
                permanentlyEnabledTeamIds={permanentlyEnabledTeamIds}
              />
            );
          })}
      </div>
    );
  },
);

const CustomSupervisorTeamMenu = React.forwardRef((props: ICustomMenuProps, ref) => {
  const { t } = useTranslation('respondentSelect');
  const menuRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    menuRef.current?.scrollTo({ top: (ref as React.MutableRefObject<number>).current });
    const handle = () => {
      if (menuRef.current) {
        (ref as React.MutableRefObject<number>).current = menuRef.current.scrollTop;
      }
    };
    if (menuRef.current) {
      menuRef.current.addEventListener('scroll', handle);
    }
    return () => {
      if (menuRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        menuRef.current.removeEventListener('scroll', handle);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <components.Menu {...props}>
      <div data-cy="admin-supervisor-team-menu" style={selectMenuStyles}>
        <div style={{ maxHeight: '250px', overflowY: 'auto' }} ref={menuRef}>
          {props.teams.map((team) => {
            return (
              <>
                <TeamItem
                  level={0}
                  key={`t_${team?.id}`}
                  team={team}
                  value={props.value}
                  onChange={props.onChange}
                  search={props.search}
                  multiselect={props.multiselect}
                  permanentlyEnabledTeamIds={props.permanentlyEnabledTeamIds}
                />
              </>
            );
          })}
          {props.teams.length === 0 && <Empty>{t('processDetail:noOptions')}</Empty>}
        </div>
      </div>
    </components.Menu>
  );
});

export default CustomSupervisorTeamMenu;
